import React, { useContext } from 'react';
import { FaXTwitter, FaLinkedin } from 'react-icons/fa6';
import { MdOutlinePhoneIphone, MdFacebook } from 'react-icons/md';
import { BsEnvelope } from 'react-icons/bs';

import { SiteSettingsContext } from '../../contexts/SiteSettingsContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { GetInternalLinkFunctionType } from '../../models/GetInternalLinkFunctionType';
import { SanityLink } from '../../models/gatsby-graphql-types';

interface SocialShareProps {
  twitterShareVia?: string;
  getInternalLink: GetInternalLinkFunctionType;
  referralCode: string;
}

const isBrowser = typeof window !== 'undefined';

const getShareUrl = (
  referralProgramLink: SanityLink,
  currency: string,
  referralCode: string,
  getInternalLink: GetInternalLinkFunctionType
) => {
  let url = '';

  if (isBrowser && referralProgramLink?.linkType === 'internal') {
    url = `${window.location.origin}${getInternalLink({
      reference: referralProgramLink.internalLink,
      currency,
    })}`;
  }

  if (referralProgramLink?.linkType === 'external') {
    url = referralProgramLink.href;
  }

  if (isBrowser && url) {
    try {
      const shareUrl = new URL(url);
      shareUrl.searchParams.append('referral_code', referralCode);

      return shareUrl.href;
    } catch (err) {
      console.error(err);
      return '';
    }
  }

  return '';
};

const SocialShare = ({
  twitterShareVia = 'invisiblefence',
  getInternalLink,
  referralCode,
}: SocialShareProps) => {
  const { currency } = useContext(LocaleContext);
  const { siteSettings } = useContext(SiteSettingsContext);
  const { tt } = useUiComponentsInternationalContext();

  const shareUrl = getShareUrl(
    siteSettings.referralProgramLink,
    currency,
    referralCode,
    getInternalLink
  );

  const shareSubject = encodeURIComponent(
    siteSettings.referralProgramShareSubject
  );
  const shareMessage = encodeURIComponent(
    siteSettings.referralProgramShareMessage
  );
  const shareEmailMessage = encodeURIComponent(
    `${siteSettings.referralProgramShareMessage}\n\n${shareUrl}`
  );

  const socials = [
    {
      outlet: tt(s => s.referralProgramComponent.emailOutletName),
      href: `mailto:?subject=${shareSubject}&body=${shareEmailMessage}`,
      label: tt(s => s.referralProgramComponent.emailOutletLabel),
      icon: <BsEnvelope size={24} aria-hidden="true" />,
    },
    {
      outlet: tt(s => s.referralProgramComponent.smsOutletName),
      href: `sms:?body=${shareEmailMessage}`,
      label: tt(s => s.referralProgramComponent.smsOutletLabel),
      icon: <MdOutlinePhoneIphone size={24} aria-hidden="true" />,
    },
    {
      outlet: tt(s => s.referralProgramComponent.facebookOutletName),
      href: `https://www.facebook.com/sharer.php?u=${shareUrl}`,
      label: tt(s => s.referralProgramComponent.facebookOutletLabel),
      icon: <MdFacebook size={24} aria-hidden="true" />,
    },
    {
      outlet: tt(s => s.referralProgramComponent.twitterOutletName),
      href: `https://x.com/intent/tweet?url=${shareUrl}&text=${shareMessage}&via=${twitterShareVia}`,
      label: tt(s => s.referralProgramComponent.twitterOutletLabel),
      icon: <FaXTwitter size={24} aria-hidden="true" />,
    },
    {
      outlet: tt(s => s.referralProgramComponent.linkedInOutletName),
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      label: tt(s => s.referralProgramComponent.linkedInOutletLabel),
      icon: <FaLinkedin size={24} aria-hidden="true" />,
    },
  ];
  return (
    <div className="flex gap-2 self-center md:self-stretch mb-2 flex-wrap">
      {socials.map(social => (
        <a
          key={`social-share-${social.label}`}
          href={social.href}
          target="_blank"
          rel="noreferrer"
          aria-label={social.label}
          className="flex md:flex-grow p-2 md:px-4 rounded-lg transition-colors bg-primary text-primary-foreground hover:bg-primary-hover no-underline gap-1 justify-center"
        >
          {social.icon}
          <span className="leading-6 hidden md:block">{social.outlet}</span>
        </a>
      ))}
    </div>
  );
};

export default SocialShare;
